import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { parse } from 'query-string';
import { object } from 'prop-types';
import {
  startSetStream,
  startEditStream,
  startSetStreamOffer,
  resetStreamOffer,
} from '@actions/streams';
import { streamConstants } from '../../../constants/streams';
import WithAlert from '@hoc/WithAlert';
import { iFMessage } from '@hoc/IntlComponents';

import { FlowPageView } from '../index';

class FlowPage extends Component {
  static propTypes = {
    stream: object.isRequired,
  };

  state = {
    loading: false,
  };

  UNSAFE_componentWillMount() {
    this.props.resetStreamOffer();
  }

  componentDidMount() {
    const { match, history, intl } = this.props;
    const params = parse(history.location.search);
    const streamId = parseInt(match.params.id, 10);

    if (params.created) {
      this.props.showAlert({
        alertType: 'success',
        message: iFMessage({ intl, id: 'flow.created-success' }),
        dismissable: false,
        closeInSeconds: 3,
      });
      window.scrollTo({top: document.body.scrollHeight, left: 0, behavior: 'smooth'});
    }

    if (params.saved) {
      this.props.showAlert({
        alertType: 'success',
        message: iFMessage({ intl, id: 'flow.alert.saved' }),
        dismissable: false,
        closeInSeconds: 3,
      });
    }

    if (!isNaN(streamId)) {
      this.setState(() => ({ loading: true }));

      this.props
        .startSetStream(streamId)
        .then(({ type, stream = {} }) => {
          if (type === streamConstants.SET_STREAM_FAILURE) {
            this.props.history.replace('/404');
            return;
          }
          this.props.startSetStreamOffer(stream.offerId);
        })
        .then(() => this.setState(() => ({ loading: false })))
        .catch(({ response }) => this.handleError(response));
    }
  }

  /**
   * Handle request error
   * @param {Object} response Error response
   */
  handleError = response => {
    if (response && response.data && response.data.error) {
      const { message: error } = response.data.error;
      this.props.showAlert({
        alertType: 'danger',
        message: error.message,
        dismissable: false,
        closeInSeconds: 5,
      });
    }

    this.setState(() => ({ loading: false }));
  };

  /**
   * Get only selected landings/prelanders
   * @param {Array} items Array of items
   * @return {Array} Array of selected items
   */
  getSelectedOnly = (items = []) =>
    items.reduce((selected, item) => (item.selected > 0 ? [...selected, item.id] : selected), []);

  /**
   * Convert trackers to Stream's type
   * @param {Array} trackers Array of trackers
   * @return {Object} Object with converted trackers
   */
  prepareTrackers = (trackers = []) => {
    const preparedTrackers = Object.keys(trackers).reduce((prepared, type, index) => {
      const trackersByType = trackers[type].reduce(
        (prepared, { id, params, type: trackersType }) => {
          const fields = params
            ? Object.keys(params).reduce(
                (items, key) => ({ ...items, [`tracking_${id}_${key}_${index}`]: params[key] }),
                {},
              )
            : {};
          if (trackersType === 'simple_img') {
            fields[`tracking_${id}_${type}`] = true;
          }

          return { ...prepared, ...fields };
        },
        {},
      );
      return {
        ...prepared,
        [type]: trackersByType,
      };
    }, {});
    return preparedTrackers;
  };

  /**
   * Convert data to Stream's type
   * @param {Object} stream Stream's data
   * @return {Object} Converted data
   */
  normalizeStream = stream => {
    const { landings, transits, domains, postback, settings, pageTracking } = stream;

    let landingsId = this.getSelectedOnly(landings);
    let transitsId = this.getSelectedOnly(transits);

    if (domains && domains.landings) {
      landingsId = [...landingsId, ...this.getSelectedOnly(domains.landings)];
    }

    if (domains && domains.transits) {
      transitsId = [...transitsId, ...this.getSelectedOnly(domains.transits)];
    }

    const trackers = this.prepareTrackers(pageTracking);
    return {
      ...stream,
      abTest: stream.editable,
      landingsId,
      transitsId,
      pixels: postback.pixels,
      statuses: postback.statuses.filter(({ value }) => value).map(({ id }) => id),
      transitFeatures: settings.transits,
      trackers,
    };
  };

  prepareUpdates = updates => {
    const obj = {};
    Object.keys(updates).forEach(key => {
      if (!/^trackers_/.test(key)) {
        obj[key] = updates[key];
      } else {
        Object.keys(updates[key]).forEach(trackEl => {
          obj[trackEl] = updates[key][trackEl];
        });
      }
    });
    return obj;
  };

  /**
   * Save a Stream
   * @param {number} streamId Stream's id
   * @param {object} updates Stream's data
   */
  handleSubmit = (streamId, updates) => {
    this.setState(() => ({ loading: true }));
    this.props
      .startEditStream(streamId, this.prepareUpdates(updates))
      .then(() => this.props.history.replace(`/tools/flows/${streamId}?saved=1`))
      .catch(({ response }) => this.handleError(response));
  };

  render() {
    const stream = this.normalizeStream(this.props.stream);
    const data = { ...this.props, ...this.state };
    return <FlowPageView data={data} stream={stream} handleSubmit={this.handleSubmit} />;
  }
}

const mapStateToProps = ({ streams }) => ({
  stream: streams.selected,
  settings: streams.settings,
  offer: streams.offer,
});

const mapDispatchToProps = dispatch => ({
  resetStreamOffer: () => dispatch(resetStreamOffer()),
  startSetStream: streamId => dispatch(startSetStream(streamId)),
  startEditStream: (streamId, updates) => dispatch(startEditStream(streamId, updates)),
  startSetStreamOffer: offerId => dispatch(startSetStreamOffer(offerId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(WithAlert(injectIntl(FlowPage)));
